import { useMemo, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { DOMAIN_LABELS } from '@/types/contextEngine'
import type { DomainScore } from '@/types/contextEngine'

interface CompletenessRadarProps {
  domainScores: DomainScore[]
  overallScore: number
  size?: number
  className?: string
}

/** Map 0–100 score to a fill color along gray → amber spectrum */
function scoreToColor(score: number): string {
  if (score <= 0) return '#374151'   // gray-700
  if (score < 25) return '#78716C'   // warm gray
  if (score < 50) return '#B45309'   // amber-700
  if (score < 75) return '#F59E0B'   // amber-500
  return '#FCD34D'                    // amber-300
}

interface WedgeProps {
  index: number
  total: number
  score: DomainScore
  cx: number
  cy: number
  maxRadius: number
  onHover: (score: DomainScore | null) => void
  onClick: (domain: string) => void
  reducedMotion: boolean
}

function Wedge({ index, total, score, cx, cy, maxRadius, onHover, onClick, reducedMotion }: WedgeProps) {
  const angleSpan = (2 * Math.PI) / total
  const startAngle = index * angleSpan - Math.PI / 2
  const endAngle = startAngle + angleSpan
  const gap = 0.02 // Small gap between wedges

  const radius = maxRadius * (Math.max(score.score, 5) / 100) // Min 5% so empty domains are visible
  const innerRadius = 30

  const path = useMemo(() => {
    const x1 = cx + innerRadius * Math.cos(startAngle + gap)
    const y1 = cy + innerRadius * Math.sin(startAngle + gap)
    const x2 = cx + radius * Math.cos(startAngle + gap)
    const y2 = cy + radius * Math.sin(startAngle + gap)
    const x3 = cx + radius * Math.cos(endAngle - gap)
    const y3 = cy + radius * Math.sin(endAngle - gap)
    const x4 = cx + innerRadius * Math.cos(endAngle - gap)
    const y4 = cy + innerRadius * Math.sin(endAngle - gap)

    const largeArc = angleSpan - 2 * gap > Math.PI ? 1 : 0

    return [
      `M ${x1} ${y1}`,
      `L ${x2} ${y2}`,
      `A ${radius} ${radius} 0 ${largeArc} 1 ${x3} ${y3}`,
      `L ${x4} ${y4}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x1} ${y1}`,
      'Z',
    ].join(' ')
  }, [cx, cy, radius, innerRadius, startAngle, endAngle, gap, angleSpan])

  const fillColor = scoreToColor(score.score)

  return (
    <motion.path
      d={path}
      fill={fillColor}
      fillOpacity={0.85}
      stroke="rgba(255,255,255,0.06)"
      strokeWidth={0.5}
      initial={reducedMotion ? {} : { opacity: 0, scale: 0.8 }}
      animate={reducedMotion ? {} : { opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.05, duration: 0.4, ease: [0, 0, 0.2, 1] }}
      whileHover={reducedMotion ? {} : { scale: 1.06, transition: { duration: 0.2 } }}
      onMouseEnter={() => onHover(score)}
      onMouseLeave={() => onHover(null)}
      onClick={() => onClick(score.domain)}
      className="cursor-pointer"
      style={{ transformOrigin: `${cx}px ${cy}px` }}
    />
  )
}

function DomainLabel({ index, total, cx, cy, radius, label }: {
  index: number; total: number; cx: number; cy: number; radius: number; label: string
}) {
  const angle = (index * (2 * Math.PI)) / total - Math.PI / 2 + Math.PI / total
  const x = cx + (radius + 18) * Math.cos(angle)
  const y = cy + (radius + 18) * Math.sin(angle)

  // Determine text-anchor based on position
  const isLeft = x < cx - 10
  const isRight = x > cx + 10
  const anchor = isLeft ? 'end' : isRight ? 'start' : 'middle'

  return (
    <text
      x={x}
      y={y}
      textAnchor={anchor}
      dominantBaseline="central"
      className="fill-text-tertiary"
      fontSize={9}
      fontFamily="var(--font-primary)"
    >
      {label}
    </text>
  )
}

export function CompletenessRadar({
  domainScores,
  overallScore,
  size = 320,
  className = '',
}: CompletenessRadarProps) {
  const [hovered, setHovered] = useState<DomainScore | null>(null)
  const navigate = useNavigate()
  const reducedMotion = useReducedMotion() ?? false

  const viewBox = 400
  const cx = viewBox / 2
  const cy = viewBox / 2
  const maxRadius = 140
  const total = 12

  const handleClick = (domain: string) => {
    navigate(`/context/domains/${domain}`)
  }

  return (
    <div className={`relative ${className}`}>
      <svg
        viewBox={`0 0 ${viewBox} ${viewBox}`}
        width={size}
        height={size}
        role="img"
        aria-label={`Context completeness radar: ${overallScore}% overall`}
      >
        {/* Glow filter */}
        <defs>
          <filter id="amber-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* Background ring */}
        <circle cx={cx} cy={cy} r={maxRadius} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth={1} />
        <circle cx={cx} cy={cy} r={maxRadius * 0.66} fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth={0.5} />
        <circle cx={cx} cy={cy} r={maxRadius * 0.33} fill="none" stroke="rgba(255,255,255,0.02)" strokeWidth={0.5} />

        {/* Wedges */}
        <g filter={overallScore > 0 ? 'url(#amber-glow)' : undefined}>
          {domainScores.map((score, i) => (
            <Wedge
              key={score.domain}
              index={i}
              total={total}
              score={score}
              cx={cx}
              cy={cy}
              maxRadius={maxRadius}
              onHover={setHovered}
              onClick={handleClick}
              reducedMotion={reducedMotion}
            />
          ))}
        </g>

        {/* Domain labels */}
        {domainScores.map((score, i) => (
          <DomainLabel
            key={score.domain}
            index={i}
            total={total}
            cx={cx}
            cy={cy}
            radius={maxRadius}
            label={DOMAIN_LABELS[score.domain]}
          />
        ))}

        {/* Center score */}
        <text
          x={cx}
          y={cy - 6}
          textAnchor="middle"
          dominantBaseline="central"
          className="fill-text-primary"
          fontSize={36}
          fontFamily="var(--font-display)"
          fontWeight={700}
        >
          {Math.round(overallScore)}
        </text>
        <text
          x={cx}
          y={cy + 18}
          textAnchor="middle"
          dominantBaseline="central"
          className="fill-text-tertiary"
          fontSize={11}
          fontFamily="var(--font-primary)"
        >
          overall
        </text>
      </svg>

      {/* Hover tooltip */}
      {hovered && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-bg-secondary border border-border rounded-lg px-3 py-2 text-xs shadow-lg pointer-events-none z-10">
          <div className="font-display font-semibold text-text-primary">
            {DOMAIN_LABELS[hovered.domain]}
          </div>
          <div className="text-text-secondary mt-0.5">
            <span className="font-mono">{Math.round(hovered.score)}%</span>
            {' · '}
            {hovered.activeChunks} chunks
            {' · '}
            conf {(hovered.avgConfidence * 100).toFixed(0)}%
          </div>
          {hovered.gaps.length > 0 && (
            <div className="text-accent-amber mt-1 truncate max-w-[200px]">
              Gap: {hovered.gaps[0]}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
